import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo, cx } from "@/lib/ui";
import { useAuth } from "@/context/Auth";

const NAV = [
  { to: "/seller/app", end: true, icon: "fa-gauge", label: "Dashboard" },
  { to: "/seller/app/products", icon: "fa-box", label: "My Products" },
  { to: "/seller/app/products/new", icon: "fa-plus", label: "Add Product" },
  { to: "/seller/app/subscription", icon: "fa-crown", label: "Subscription" },
  { to: "/seller/app/crm", icon: "fa-headset", label: "Leads CRM" },
  { to: "/seller/app/history", icon: "fa-clock-rotate-left", label: "History" },
  { to: "/seller/app/profile", icon: "fa-building", label: "Company Profile" },
  { to: "/seller/app/more", icon: "fa-ellipsis", label: "Badge · Billing · Support" },
];

export default function SellerLayout() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [open, setOpen] = useState(false);

  const navItems = [...NAV];
  if (user?.seller_type === "retailer") {
    navItems.splice(1, 0, { to: "/seller/app/wholesalers", icon: "fa-store", label: "Wholesalers Listing" });
  }

  useEffect(() => {
    api.get("/public/holidays").then(({ data }) => setHolidays(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      {/* Sidebar */}
      <aside className={cx(
        "fixed inset-y-0 left-0 z-40 w-64 transform border-r border-slate-800 bg-slate-950 p-4 text-slate-300 transition lg:static lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="mb-6"><Logo dark /></div>
        <div className="mb-4 rounded-lg bg-white/5 p-3">
          <p className="text-xs uppercase tracking-wider text-slate-500">Signed in</p>
          <p className="truncate text-sm font-semibold text-white">{user?.company_name || user?.phone}</p>
          <p className="text-xs capitalize text-orange-400">{user?.seller_type}</p>
        </div>
        <nav className="space-y-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`seller-nav-${n.label.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) => cx(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
                isActive ? "bg-orange-600 text-white" : "hover:bg-white/5"
              )}
            >
              <i className={`fa-solid ${n.icon} w-4`} /> {n.label}
            </NavLink>
          ))}
          <button
            onClick={() => { logout(); nav("/"); }}
            data-testid="seller-logout"
            className="mt-4 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-red-300 hover:bg-red-500/10"
          >
            <i className="fa-solid fa-arrow-right-from-bracket w-4" /> Logout
          </button>
        </nav>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
          <button onClick={() => setOpen(true)}><i className="fa-solid fa-bars text-slate-700" /></button>
          <Logo />
        </header>

        {holidays.length > 0 && (
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800" data-testid="holiday-notice">
            <i className="fa-solid fa-calendar-day mr-1" />
            <b>Admin Holiday Notice:</b> {holidays[0].name} ({holidays[0].from_date} → {holidays[0].to_date}). {holidays[0].note}
          </div>
        )}

        <main className="mx-auto max-w-6xl px-4 py-6"><Outlet /></main>
      </div>
    </div>
  );
}
